import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { getUrl, ResourceUri } from '@ngxp/rest';
import { doIfLoadingRequired, StateResource } from '@schir-int-client/ngrx-helpers';
import { combineLatest, Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import {
	cancelSignaturAction,
	createSignaturTaskAction,
	downloadSignaturAction,
	downloadSignaturTaskAction,
	downloadSignerScriptAction,
	loadSignaturValidatorResponseAction,
	openInProgressDialogAction,
} from './signatur.actions';
import { SignaturLinkRel } from './signatur.linkrel';
import { SignatureResource, SignatureValidatorResponseResource, SignaturTaskResource } from './signatur.model';
import {
	inProgressDialogOpenSelector,
	signatureByReferenceSelector,
	signatureTaskSelector,
	signatureValidatorResponsesSelector,
} from './signature.selectors';
import { changeEntryListSelector, ChangeEntryResource } from '@schir-int-client/register-change';
import { SignaturState } from './signatur.reducer';

@Injectable({ providedIn: 'root' })
export class SignaturFacade {
	constructor(private store: Store<SignaturState>) {}

	createSignaturTask(changeEntryUris: ResourceUri[], skipCheck: boolean = false) {
		this.store.dispatch(createSignaturTaskAction({ changeEntryUris, skipCheck }));
	}

	downloadSignatureTask() {
		this.store.dispatch(downloadSignaturTaskAction());
	}

	getSignatureTask(): Observable<SignaturTaskResource> {
		return this.store.select(signatureTaskSelector);
	}

	downloadSignerScript() {
		this.store.dispatch(downloadSignerScriptAction());
	}

	cancelSignature() {
		this.store.dispatch(cancelSignaturAction());
	}

	getInProgressDialogIsOpen(): Observable<boolean> {
		return this.store.select(inProgressDialogOpenSelector);
	}

	openInProgressDialog() {
		this.store.dispatch(openInProgressDialogAction());
	}

	getSignature(changeEntry: ChangeEntryResource): Observable<SignatureResource> {
		return this.store
			.select(signatureByReferenceSelector(getUrl(changeEntry, SignaturLinkRel.DOWNLOAD_SIGNATURE)))
			.pipe(
				filter(
					stateResource =>
						!doIfLoadingRequired(stateResource, () =>
							this.store.dispatch(downloadSignaturAction({ changeEntry }))
						)
				),
				map(stateResource => stateResource.resource)
			);
	}

	getSignatureValidatorResponses(): Observable<StateResource<SignatureValidatorResponseResource>> {
		return combineLatest([
			this.store.select(changeEntryListSelector),
			this.store.select(signatureValidatorResponsesSelector),
		]).pipe(
			filter(([entryList]) => entryList.loaded),
			filter(
				([, selected]) =>
					!doIfLoadingRequired(selected, () => this.store.dispatch(loadSignaturValidatorResponseAction()))
			),
			map(([, selected]) => selected)
		);
	}
}
